import { getAvailableGarnets, getPlayer } from "../appliers";
import type {
  BidHiddenCubeAction,
  DecideHiddenCubeAction,
  MinusAuctionAction,
  MinusAuctionState,
  PassHiddenCubeBidAction,
  RejectCubeAction,
  TransferGarnetAction,
  ValidationResult,
} from "../types";

export function validateAction(
  state: MinusAuctionState,
  action: MinusAuctionAction,
): ValidationResult {
  if (action.type === "RejectCube") {
    return validateRejectCube(state, action);
  }
  if (action.type === "TakeCube") {
    return validateAuctionActor(state, action.playerId);
  }
  if (action.type === "TransferGarnet") {
    return validateTransferGarnet(state, action);
  }
  if (action.type === "BidHiddenCube") {
    return validateBidHiddenCube(state, action);
  }
  if (action.type === "PassHiddenCubeBid") {
    return validatePassHiddenCubeBid(state, action);
  }
  if (action.type === "DecideHiddenCube") {
    return validateDecideHiddenCube(state, action);
  }

  return { valid: false, error: "Unknown action" };
}

export function validateRejectCube(
  state: MinusAuctionState,
  action: RejectCubeAction,
): ValidationResult {
  const actor = validateAuctionActor(state, action.playerId);
  if (!actor.valid) {
    return actor;
  }

  const player = getPlayer(state, action.playerId);
  if (action.resource === "CHIP") {
    return player.chips > 0
      ? { valid: true }
      : { valid: false, error: "No chips available" };
  }

  if (player.chips > 0) {
    return {
      valid: false,
      error: "Garnets can only be used when the player has no chips",
    };
  }

  return player.garnets > 0
    ? { valid: true }
    : { valid: false, error: "No garnets available" };
}

export function validateTransferGarnet(
  state: MinusAuctionState,
  action: TransferGarnetAction,
): ValidationResult {
  if (
    state.phase !== "AUCTION_DECISION" &&
    state.phase !== "HIDDEN_AUCTION_BIDDING"
  ) {
    return { valid: false, error: "Garnets cannot be transferred now" };
  }
  if (action.fromPlayerId === action.toPlayerId) {
    return { valid: false, error: "Cannot transfer garnets to self" };
  }
  if (!Number.isInteger(action.amount) || action.amount < 1) {
    return {
      valid: false,
      error: "Transfer amount must be a positive integer",
    };
  }
  if (
    !hasPlayer(state, action.fromPlayerId) ||
    !hasPlayer(state, action.toPlayerId)
  ) {
    return { valid: false, error: "Unknown transfer player" };
  }

  const available = getAvailableGarnets(state, action.fromPlayerId);
  return available >= action.amount
    ? { valid: true }
    : { valid: false, error: "Not enough transferable garnets" };
}

export function validateBidHiddenCube(
  state: MinusAuctionState,
  action: BidHiddenCubeAction,
): ValidationResult {
  const hiddenAuction = state.hiddenAuction;
  if (state.phase !== "HIDDEN_AUCTION_BIDDING" || !hiddenAuction) {
    return { valid: false, error: "Hidden tile bidding is not active" };
  }
  if (hiddenAuction.currentBidderId !== action.playerId) {
    return { valid: false, error: "It is not this player's hidden bid turn" };
  }
  if (hiddenAuction.passedPlayerIds.includes(action.playerId)) {
    return { valid: false, error: "Player already passed this hidden auction" };
  }
  if (
    !Number.isInteger(action.amount) ||
    action.amount <= hiddenAuction.currentBid
  ) {
    return {
      valid: false,
      error: "Bid must be at least one garnet higher than the current bid",
    };
  }

  const available = getAvailableGarnets(state, action.playerId);
  return available >= action.amount
    ? { valid: true }
    : { valid: false, error: "Not enough available garnets for bid" };
}

export function validatePassHiddenCubeBid(
  state: MinusAuctionState,
  action: PassHiddenCubeBidAction,
): ValidationResult {
  const hiddenAuction = state.hiddenAuction;
  if (state.phase !== "HIDDEN_AUCTION_BIDDING" || !hiddenAuction) {
    return { valid: false, error: "Hidden tile bidding is not active" };
  }
  if (hiddenAuction.currentBidderId !== action.playerId) {
    return { valid: false, error: "It is not this player's hidden bid turn" };
  }
  if (hiddenAuction.passedPlayerIds.includes(action.playerId)) {
    return { valid: false, error: "Player already passed this hidden auction" };
  }
  return { valid: true };
}

export function validateDecideHiddenCube(
  state: MinusAuctionState,
  action: DecideHiddenCubeAction,
): ValidationResult {
  const hiddenAuction = state.hiddenAuction;
  if (state.phase !== "HIDDEN_AUCTION_DECISION" || !hiddenAuction) {
    return { valid: false, error: "Hidden tile decision is not active" };
  }
  if (hiddenAuction.highestBidderId !== action.playerId) {
    return { valid: false, error: "Only the hidden tile winner can decide" };
  }
  return { valid: true };
}

function validateAuctionActor(
  state: MinusAuctionState,
  playerId: string,
): ValidationResult {
  if (state.phase !== "AUCTION_DECISION" || !state.auction) {
    return { valid: false, error: "Auction decision is not active" };
  }
  if (state.auction.currentPlayerId !== playerId) {
    return { valid: false, error: "It is not this player's auction turn" };
  }
  return { valid: true };
}

function hasPlayer(state: MinusAuctionState, playerId: string): boolean {
  return state.players.some((player) => player.playerId === playerId);
}
